<script setup lang="ts">
import { ref, defineProps, toRef, computed } from "vue";
import {
  ContainerProperties,
  useStarsPositions,
} from "@/composables/useStarsPositions";

const props = defineProps<{
  azimuthFacade: number;
  stars: object[];
  color: string;
}>();

const starsContainerRef = ref<HTMLElement | null>(null);

const getStarsContainerProps = computed<ContainerProperties | null>(() => {
  const newRect = starsContainerRef.value?.getBoundingClientRect() ?? null;
  if (!newRect) {
    return null;
  }
  const size = newRect.width; // т.к. aspect-square → width === height
  const halfSize = size / 2;
  const itemHalf = 24; // size-12 = 3rem = 48px → половина 24px

  return {
    centerX: halfSize,
    centerY: halfSize,
    radius: halfSize - itemHalf - 8, // запас 8px, чтобы не касались края
  };
});

const { starsPositions, starsContainerProperties, getStarIndex } =
  useStarsPositions(
    toRef(props, "azimuthFacade"),
    () => getStarsContainerProps.value
  );

function disableAppearTransition(el: HTMLElement) {
  el.style.transition = "none";
}

function enableAppearTransition(el: HTMLElement) {
  el.style.transition = "left 0.6s ease-out, top 0.6s ease-out";
}
</script>

<template>
  <div
    ref="starsContainerRef"
    class="absolute top-1/2 left-1/2 z-[800] translate-x-[-50%] translate-y-[-50%] select-none h-1/2 aspect-square rounded-full"
    :style="`color: ${color}`"
  >
    <TransitionGroup
      tag="div"
      name="star-pos"
      :css="true"
      appear
      @before-appear="disableAppearTransition"
      @after-appear="enableAppearTransition"
    >
      <div
        v-for="(starPosition, i) in starsPositions"
        :key="i"
        class="star-container"
        :style="{
          left: starPosition.x + 'px',
          top: starPosition.y + 'px',
          transform: 'translate(-50%, -50%)',
          'border-color': color,
        }"
      >
        <div>{{ stars[getStarIndex(i)].sk }}</div>
        <div>{{ stars[getStarIndex(i)].sf }}</div>
        <div class="col-span-2">{{ stars[getStarIndex(i)].sb }}</div>
        <div>{{ stars[getStarIndex(i)].sy }}</div>
        <div>{{ stars[getStarIndex(i)].sm }}</div>
      </div>
    </TransitionGroup>
    <div
      class="star-container"
      :style="{
        left: starsContainerProperties.centerX + 'px',
        top: starsContainerProperties.centerY + 'px',
        transform: 'translate(-50%, -50%)',
        'border-color': color,
      }"
    >
      <div>{{ stars[9].sk }}</div>
      <div>{{ stars[9].sf }}</div>
      <div class="col-span-2">{{ stars[9].sb }}</div>
      <div>{{ stars[9].sy }}</div>
      <div>{{ stars[9].sm }}</div>
    </div>
  </div>
</template>

<style scoped>
.star-container {
  @apply absolute w-10 h-16 border-2 grid grid-cols-2 items-center justify-center text-sm font-bold;
}
</style>
